import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetServiceOnTimeResponse } from "subModule/src/services/analytics/interface";
import { useGetServiceOnTimeService } from "subModule/src/services/analytics/useAnalytics";
import { PieChart } from "@mui/x-charts/PieChart";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import ComparisonChange from "../Analytics/ComparisonChange";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetTitleSx,
  dashboardWidgetIconSx,
  dashboardWidgetTitleRowSx,
} from "components/Analytics/dashboardWidgetLayout";

interface ServiceOnTimePeriodData {
  totalInvoices?: number;
  onTimeCount?: number;
  extraTimeCount?: number;
  onTimeCountPercentage?: number;
  extraTimeCountPercentage?: number;
  comparisonPercentage?: number;
}

function resolveServiceOnTimeData(
  raw: any,
  range?: string,
): ServiceOnTimePeriodData {
  if (!raw || typeof raw !== "object") {
    return {};
  }

  const rangeKey = range || "monthly";
  const nested =
    raw[rangeKey] ||
    raw.yearly ||
    raw.monthly ||
    raw.weekly ||
    raw.daily ||
    null;

  const period =
    nested &&
    typeof nested === "object" &&
    ("onTimeCount" in nested || "extraTimeCount" in nested)
      ? nested
      : raw;

  return {
    totalInvoices: Number(period?.totalInvoices ?? 0),
    onTimeCount: Number(period?.onTimeCount ?? 0),
    extraTimeCount: Number(period?.extraTimeCount ?? 0),
    onTimeCountPercentage: period?.onTimeCountPercentage,
    extraTimeCountPercentage: period?.extraTimeCountPercentage,
    comparisonPercentage: raw?.comparison?.comparisonPercentage,
  };
}

interface ServiceOnTimeProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleStationActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

const compactSelectSx = {
  "& .css-7896aj-control, & .css-1xiok1u-control": {
    minHeight: "auto",
    fontSize: '9px',
    height: "auto",
    overflow: "hidden",
    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
      {
        background: "#fff",
        padding: 0,
        width: "15px",
        height: "15px",
      },
    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator": {
      position: "absolute",
      right: 0,
    },
    border: 0,
  },
};

const formatCount = (value: number) =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 2,
  }).format(Math.abs(value || 0));

export default function ServiceOnTime(props: ServiceOnTimeProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const [data, setData] = useState<ServiceOnTimePeriodData | null>(null);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px)",
  );
  const [loading, setLoading] = useState(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const primary = props.themes?.primary || "#7C3AED";
  const secondary = props.themes?.secondary || "#F97316";

  const { refetch: apiRefetch } = useGetServiceOnTimeService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);

  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);

  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    const rangeValue = watch("range")?.value;
    await apiRefetch()
      .then((res: GetServiceOnTimeResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(resolveServiceOnTimeData(res?.data?.data || {}, rangeValue));
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const onTime = Number(data?.onTimeCount || 0);
  const extraTime = Number(data?.extraTimeCount || 0);
  const allZero = onTime + extraTime === 0;

  const chartData = [
    {
      id: 0,
      label: t("onTimeCount"),
      value: allZero ? 1 : Math.abs(onTime),
      color: primary,
    },
    {
      id: 1,
      label: t("extraTimeCount"),
      value: allZero ? 1 : Math.abs(extraTime),
      color: secondary,
    },
  ];

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 250,
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        borderRadius: "12px",
        border: "1px solid #EEF0F3",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0, mb: 1.5 }}
        >
          <Box sx={dashboardWidgetTitleRowSx}>
            <Box
              sx={{
                ...dashboardWidgetIconSx,
                width: 36,
                height: 36,
                borderRadius: "10px",
                backgroundColor: `${primary}1F`,
                color: primary,
                
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: 17, color: primary },
              }}
            >
              <AccessTimeOutlinedIcon />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: { xs: 12, sm: 15 },
              fontWeight: { xs: 600, sm: 700 },
              textAlign: "left",
                  color: "#0F2744",
                  lineHeight: 1.25,
                }}
              >
                {t("serviceOnTime")}
              </Typography>
              <Typography
                className="font-Saira text-left"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#9CA3AF",
                  lineHeight: 1.3,
                }}
              >
                {t("vehicleServicePerformance")}
              </Typography>
            </Box>
          </Box>

          <Box className="flex" sx={{ flexShrink: 0 }}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box sx={compactSelectSx}>
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "serviceOnTime"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleStationActions(
                          "serviceOnTime",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleStationActions(
                            "serviceOnTime",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleStationActions("serviceOnTime", "loadNext");
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    ...compactSelectSx,
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      ...compactSelectSx[
                        "& .css-7896aj-control, & .css-1xiok1u-control"
                      ],
                      width: screenWidth ? "60px" : "auto",
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <Box
            className="flex flex-col sm:flex-row items-center gap-3"
            sx={{ flex: 1 }}
          >
            <Skeleton
              variant="circular"
              width={130}
              height={130}
              sx={{ backgroundColor: secondary, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, width: "100%" }}>
              <Skeleton sx={{ backgroundColor: secondary, mb: 1 }} />
              <Skeleton sx={{ backgroundColor: secondary, mb: 1 }} />
              <Skeleton sx={{ backgroundColor: secondary }} />
            </Box>
          </Box>
        ) : (
          <Box
            className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4"
            sx={{ flex: 1, minHeight: 0 }}
          >
            <Box
              sx={{
                position: "relative",
                width: 140,
                height: 140,
                flexShrink: 0,
              }}
            >
              <PieChart
                height={140}
                width={140}
                series={[
                  {
                    data: chartData,
                    valueFormatter: (v) =>
                      `${allZero ? 0 : v.value?.toLocaleString() || 0}`,
                    innerRadius: 44,
                    outerRadius: 62,
                    cx: 70,
                    cy: 70,
                    paddingAngle: 1.5,
                    cornerRadius: 3,
                  },
                ]}
                slotProps={{ legend: { hidden: true } }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 25,
                    fontWeight: 800,
                    color: "#0F2744",
                    lineHeight: 1,
                  }}
                >
                  {formatCount(onTime)}
                </Typography>
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6B7280",
                    mt: 0.25,
                  }}
                >
                  {t("onTime")}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minWidth: 0,
              }}
            >
              <Tooltip title={onTime.toLocaleString()} enterDelay={0}>
                <Box
                  className="flex items-center justify-between gap-2"
                  sx={{ py: 1 }}
                >
                  <Box sx={dashboardWidgetTitleRowSx}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: primary,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      className="font-Saira"
                      sx={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}
                    >
                      {t("onTimeCount")}
                    </Typography>
                  </Box>
                  <Typography
                    className="font-Saira"
                    sx={{ fontSize: 14, fontWeight: 700, color: "#0F2744" }}
                  >
                    {formatCount(onTime)}
                  </Typography>
                </Box>
              </Tooltip>

              <Divider sx={{ borderColor: "#F1F5F9" }} />

              <Tooltip title={extraTime.toLocaleString()} enterDelay={0}>
                <Box
                  className="flex items-center justify-between gap-2"
                  sx={{ py: 1 }}
                >
                  <Box sx={dashboardWidgetTitleRowSx}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: secondary,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      className="font-Saira"
                      sx={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}
                    >
                      {t("extraTimeCount")}
                    </Typography>
                  </Box>
                  <Typography
                    className="font-Saira"
                    sx={{ fontSize: 14, fontWeight: 700, color: secondary }}
                  >
                    {formatCount(extraTime)}
                  </Typography>
                </Box>
              </Tooltip>

              <Divider sx={{ borderColor: "#F1F5F9", mb: 1 }} />

              <ComparisonChange
                percentage={data?.comparisonPercentage}
                range={values[1]?.value}
                dense
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
